import { Link, Head } from '@inertiajs/react';
import { PageProps } from '@/types';
import { Icon } from '@iconify/react';
import Header from '@/Layouts/Partials/Header';
import Footer from '@/Layouts/Partials/Footer';
import { SettingModel } from '@/types/models/setting';
import FrontLayout from '@/Layouts/FrontLayout';
import { ComponentType, Suspense, lazy, useEffect, useState } from 'react';
import HeroSlider from '@/Components/Homepage/HeroSlider';
import SliderWithDescription from '@/Components/Homepage/SliderWithDescription';
import ChoiceOnMedal from '@/Components/Homepage/ChoiceOnMedal';
import LandscapeWithBox from '@/Components/Homepage/LandscapeWithBox';
import IconsWithTexts from '@/Components/Homepage/IconsWithTexts';
import InvestmentOnMedal from '@/Components/Homepage/InvestmentOnMedal';
import FindApartment from '@/Components/Homepage/FindApartment';
import Facilities from '@/Components/Homepage/Facilities';
import ApartmentSearch from '@/Components/Homepage/ApartmentSearch';
import { SearchEngineData } from '@/types/searchEngineData';
import { GalleryModel } from '@/types/models/gallery';
import GallerySlider from '@/Components/Homepage/GallerySlider';
import News from '@/Components/Homepage/News';
import { PostModel } from '@/types/models/post';
import AboutInvestor from '@/Components/Homepage/AboutInvestor';
import ContactForm from '@/Components/Universal/ContactForm';
import { GalleryCategory } from '@/types/models/galleryCategory';
import TopHero from '@/Components/Universal/TopHero';
import CategoriesLoop from '@/Components/Gallery/CategoriesLoop';
import Container from '@/Components/Grid/Container';
import Row from '@/Components/Grid/Row';
import Column from '@/Components/Grid/Column';
import useIsServer from '@/Hooks/useIsServer';

interface ParsedJsonTextInterface {
    [key: string]: string
}

export default function Gallery({ post }: PageProps<{
    post: PostModel
}>) {
    const getFormat = (filename: string) => {
        return filename.split('.').pop() as string;
    };

    const [parsedText, setParsedText] = useState<ParsedJsonTextInterface>();
    const isServer = useIsServer();

    useEffect(() => {
        if (post.content != '') {
            setParsedText(JSON.parse(post.content))
        }
    }, []);

    const [videoId, setVideoId] = useState<string>('')

    useEffect(() => {
        const { video } = post

        if (!video) {
            return;
        }

        const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=|.*youtu\.be\/)|youtu\.be\/)([^"&?\/ ]{11})/;

        const match = video.match(youtubeRegex);

        if (match && match[1]) {
            const extractedVideoId = match[1];
            setVideoId(extractedVideoId);
        }
    }, [])

    const Loader = () => {
        return (
            <div className='w-10 h-10 border-[2px] bg-accordorange rounded-full animate-spin'></div>
        )
    }

    const IframeSection = ({ videoId }: { videoId: string }) => {
        const [Component, setComponent] = useState<ComponentType | null>(null);

        useEffect(() => {
            // @ts-ignore
            setComponent(() => lazy(() => import('./IframeSection')))
        }, [])


        if (!Component) {
            return <Loader />;
        }

        return (
            <Suspense fallback={<Loader />}>
                {/* @ts-ignore */}
                <Component videoId={videoId} />
            </Suspense>
        )
    }

    useEffect(() => {
        if (post.slug === 'zapraszamy-na-dzien-otwarty-w-sobote-24-maja') {
            const container = document.getElementById('bookero-container');

            if (container) {
                // @ts-ignore
                if (window.bookero_config) {
                    const script = document.createElement('script');
                    script.src = 'https://cdn.bookero.pl/plugin/v2/js/bookero-compiled.js';
                    script.async = true;
                    document.body.appendChild(script);
                }
            }
        }
    }, [post.slug]);

    return (
        <>
            <Head title="Osiedle Kusocińskiego">
                <meta name='description' content='Osiedle Kusocińskiego' />
            </Head>
            <FrontLayout>
                <TopHero image='general-breadcrumbs-bg.webp' text='Aktualności' />
                <Container className='mt-20 mb-8 maxMd:my-8'>
                    <Row>
                        <Column>
                            <div className="px-8">
                                <p className='uppercase text-label leading-label mb-2 text-secondary font-extrabold'>Data dodania: {post.created_at}</p>
                                <h2 className='text-h2 leading-h2 text-primary font-extrabold mb-8 maxMd:mb-6'>{post.name}</h2>
                                <div className='text-p text-text leading-p' dangerouslySetInnerHTML={{ __html: post.excerpt }}></div>
                            </div>
                        </Column>
                    </Row>
                </Container>
                {post.video &&
                    <Container>
                        <Row>
                            <Column>
                                <IframeSection videoId={videoId} />
                            </Column>
                        </Row>
                    </Container>
                }
                <Container className='mt-20 mb-24 maxMd:my-14'>
                    <Row>
                        <Column>
                            <div className="px-8 maxMd:px-0">
                                <div className="content-area">
                                    {parsedText != undefined && Object.keys(parsedText).map((c, i) => {
                                        if (c.includes('content')) {
                                            return (
                                                <div key={c} className='text-p leading-p text-text [&>p]:mb-6 maxMd:[&>p]:mb-4' dangerouslySetInnerHTML={{ __html: parsedText[c] }}></div>
                                            )
                                        }
                                        if (c.includes('photo')) {
                                            return (
                                                <picture key={c}>
                                                    <source
                                                        srcSet={`${parsedText[c].replace(/\.[^.]+$/, '')}.webp`}
                                                        type="image/webp"
                                                    />
                                                    <source
                                                        srcSet={`${parsedText[c].replace(/\.[^.]+$/, '')}.${getFormat(parsedText[c])}`}
                                                        type={`image/${getFormat(parsedText[c])}`}
                                                    />
                                                    <img className={`w-full h-auto maxMd:mb-4 object-contain mx-auto object-center mb-6`} src={parsedText[c]} alt={post.name} />
                                                </picture>
                                            )
                                        }
                                    })}
                                </div>

                            </div>
                        </Column>
                        {post.slug === 'zapraszamy-na-dzien-otwarty-w-sobote-24-maja' &&
                            <Column className='px-8 maxMd:px-4 py-10'>
                                <div id="bookero-container"></div>
                            </Column>
                        }
                        {post.slug === 'zaprojektuj-murale-inwestycji-kusocinskiego-konkurs-dla-studentow-asp' &&
                            <Column className='px-8 maxMd:px-4 mb-20 maxMd:mb-10'>
                                <p className='text-h3 text-primary font-bold my-6'>Materiały do pobrania:</p>
                                <a download className='flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline' href="/konkurs/regulamin-konkursu-mural-kusocinskiego.pdf">
                                    <Icon icon="mdi:file-outline" width="24" height="24" />
                                    Regulamin Konkursu Mural Kusocińskiego
                                </a>
                                <a download className='flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline' href="/konkurs/zalacznik-nr-1-wymiary-sciany-elewacyjnej-budynek-a-cad.dwg">
                                    <Icon icon="mdi:file-outline" width="24" height="24" />
                                    Załącznik nr 1 - Wymiary ściany elewacyjnej - budynek A - cad
                                </a>
                                <a download className='flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline' href="/konkurs/zalacznik-nr-1-wymiary-sciany-elewacyjnej-budynek-a.pdf">
                                    <Icon icon="mdi:file-outline" width="24" height="24" />
                                    Załącznik nr 1 - Wymiary Ściany Elewacyjnej - budynek A - pdf
                                </a>
                                <a download className='flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline' href="/konkurs/zalacznik-nr-2-wymiary-sciany-elewacyjnej-budynek-b-cad.dwg">
                                    <Icon icon="mdi:file-outline" width="24" height="24" />
                                    Załącznik nr 2 - Wymiary ściany elewacyjnej - budynek B - cad
                                </a>
                                <a download className='flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline' href="/konkurs/zalacznik-nr-2-wymiary-sciany-elewacyjnej-budynek-b.pdf">
                                    <Icon icon="mdi:file-outline" width="24" height="24" />
                                    Załącznik nr 2 - Wymiary ściany elewacyjnej - budynek B - pdf
                                </a>
                                <a download className='flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline' href="/konkurs/zalacznik-nr-3-brief-konkursowy.pdf">
                                    <Icon icon="mdi:file-outline" width="24" height="24" />
                                    Załącznik nr 3 - Brief konkursowy
                                </a>
                                <a download className='flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline' href="/konkurs/zalacznik-nr-4-logotyp-inwestycji.zip">
                                    <Icon icon="mdi:file-outline" width="24" height="24" />
                                    Załącznik nr 4 - Logotyp inwestycji
                                </a>
                                <a download className='flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline' href="/konkurs/zalacznik-nr-5-wizualizacje-inwestycji.zip">
                                    <Icon icon="mdi:file-outline" width="24" height="24" />
                                    Załącznik nr 5 - Wizualizacje inwestycji
                                </a>
                                <a download className='flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline' href="/konkurs/zalacznik-nr-6-oswiadczenie-o-nieposiadaniu-zarejestrowanej-dzialalnosci.odt">
                                    <Icon icon="mdi:file-outline" width="24" height="24" />
                                    Załącznik nr 6 - Oświadczenie o nieposiadaniu zarejestrowanej działalności
                                </a>
                            </Column>
                        }
                        <Column>
                            <div className="px-8 maxMd:px-0">
                                <div className="content-area">
                                    <picture>
                                        <source
                                            srcSet={`${post.photo.replace(/\.[^.]+$/, '')}.webp`}
                                            type="image/webp"
                                        />
                                        <source
                                            srcSet={`${post.photo.replace(/\.[^.]+$/, '')}.${getFormat(post.photo)}`}
                                            type={`image/${getFormat(post.photo)}`}
                                        />
                                        <img className={`w-full max-h-[500px] maxMd:max-h-[220px] object-contain object-center mb-10 maxMd:mb-6`} src={post.photo} alt={post.name} />
                                    </picture>
                                </div>
                            </div>
                        </Column>

                        <Column>
                            <div className="px-8 maxMd:px-0">
                                <div className="content-area">
                                    <Link href={route('frontend.news')} className='button-outline max-w-[310px] ml-auto mt-2'>
                                        Wróc do listy wpisów
                                        <Icon icon={`streamline:return-2`} className='ml-1' />
                                    </Link>
                                </div>
                            </div>
                        </Column>
                    </Row>
                </Container>
                <ContactForm smallerMargin />
            </FrontLayout>
        </>
    );
}
